/*
 * Purpose: Workspace picker after sign-in (multitenancy)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 90
 */
import { Building2, Check } from 'lucide-react';
import { AuthButton, AuthCard } from '@/components/auth/ui/AuthUi';
import { useAuthStore } from '@/stores/authStore';
import type { AuthTenant } from '@/lib/auth-tenants';
import { cn } from '@/lib/utils';

interface TenantPickerPanelProps {
  onComplete?: () => void;
  next?: string;
}

export function TenantPickerPanel({ onComplete, next = '/dashboard' }: TenantPickerPanelProps) {
  const tenants = useAuthStore((s) => s.tenants);
  const activeTenant = useAuthStore((s) => s.activeTenant);
  const selectTenant = useAuthStore((s) => s.selectTenant);

  function choose(tenant: AuthTenant) {
    void selectTenant(tenant.id).then(() => {
      onComplete?.();
      if (!onComplete) window.location.href = next;
    });
  }

  return (
    <AuthCard>
      <div className="text-center mb-2">
        <h1 className="text-xl font-semibold text-[var(--auth-fg)]">Choose a workspace</h1>
        <p className="mt-1 text-sm text-[var(--auth-muted)]">
          Missions, secrets, and connectors are scoped to the workspace you select.
        </p>
      </div>

      <ul className="space-y-2" role="listbox" aria-label="Workspaces">
        {tenants.map((tenant) => {
          const selected = activeTenant?.id === tenant.id;
          return (
            <li key={tenant.id}>
              <button
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => choose(tenant)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border px-3 py-3 text-left transition-colors',
                  selected
                    ? 'border-[var(--auth-accent)] bg-[var(--auth-accent)]/10'
                    : 'border-[var(--auth-border)] hover:border-[var(--auth-accent)]/40 hover:bg-[var(--auth-bg)]'
                )}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--auth-accent)]/15 text-[var(--auth-accent)]">
                  <Building2 className="h-4 w-4" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-[var(--auth-fg)] truncate">
                    {tenant.name}
                  </span>
                  <span className="block text-xs text-[var(--auth-muted)]">
                    {tenant.slug} · {tenant.role}
                  </span>
                </span>
                {selected ? <Check className="h-4 w-4 shrink-0 text-[var(--auth-accent)]" /> : null}
              </button>
            </li>
          );
        })}
      </ul>

      {activeTenant ? (
        <AuthButton onClick={() => choose(activeTenant)}>Continue with {activeTenant.name}</AuthButton>
      ) : (
        <p className="text-xs text-center text-[var(--auth-muted)]">Select a workspace to continue.</p>
      )}
    </AuthCard>
  );
}

export default TenantPickerPanel;
